// Claude: client-side verification check — mirrors backend verification.util.ts 28-02-2026

// HOW THE DOMAIN MATCH WORKS
// ---------------------------
// 1. Strip protocol and path from the URL — take only the host part.
// 2. Split by "." and drop the last segment (the TLD, e.g. "com", "in", "uk").
// 3. Keep only tokens with 3 or more characters.
//    — WHY 3 CHARS: removes all 2-char country-code fragments (co, uk, in, de,
//      au, fr ...) that appear in multi-part TLDs like .co.in or .co.uk, while
//      still retaining short but legitimate brand names (ibm, sap, att, abc).
//    — WHY NOT 4 CHARS: a 4-char minimum would incorrectly exclude valid 3-char
//      brand names like ibm, sap, att, abc.
//    — WHY NOT A BLOCKLIST: org / net / gov / edu are virtually always the TLD
//      (last segment), which is already dropped before the length filter runs,
//      so they never reach the filter. No blocklist maintenance needed.
// 4. Check if any token from the email domain matches any token from the website.

const PERSONAL_EMAIL_DOMAINS = [
  'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'icloud.com',
  'live.com', 'yahoo.in', 'rediffmail.com', 'sify.com', 'mail.ru',
  'yandex.ru', 'qq.com', '163.com', '126.com', 'web.de', 'gmx.de',
  'laposte.net', 'wanadoo.fr',
];

/**
 * Extracts brand tokens from a domain or URL.
 * Strips protocol + path, splits by dot, drops the TLD (last part),
 * and keeps tokens with 3+ characters.
 *
 * Examples:
 *   "https://engg.abc.com/about"  →  ["engg", "abc"]
 *   "abc.com"                     →  ["abc"]
 *   "abc.co.in"                   →  ["abc"]       (co=2 chars, filtered out)
 *   "portal.engg.acme.co.in"      →  ["portal", "engg", "acme"]
 *   "ibm.com"                     →  ["ibm"]       (3 chars — kept correctly)
 */
function extractBrandTokens(domainOrUrl: string): string[] {
  if (!domainOrUrl) return [];
  let s = domainOrUrl.trim().toLowerCase();
  s = s.replace(/^https?:\/\//, '').replace(/^ftp:\/\//, '');
  // Take only the host part (before first "/", "?", "#")
  s = s.split('/')[0].split('?')[0].split('#')[0];
  const parts = s.split('.');
  // Drop TLD (last part), keep tokens with 3+ characters.
  // 2-char country codes (co, uk, in, de, au...) are filtered out.
  // 3-char brand names (ibm, sap, abc...) are retained.
  return parts.slice(0, -1).filter((t) => t.length >= 3);
}

function isPersonalDomain(email: string): boolean {
  const domain = (email || '').toLowerCase().split('@')[1] || '';
  return PERSONAL_EMAIL_DOMAINS.includes(domain);
}

export interface VerificationCheckResult {
  status: 'verified' | 'unverified';
  reason?: string; // shown in admin warning modal and upload reports
}

/**
 * Checks whether the email domain matches any brand token from the company website.
 * Returns verified if any 3+ character token overlaps; unverified with a reason otherwise.
 */
export function runVerificationCheck(email: string, website: string): VerificationCheckResult {
  if (!email || isPersonalDomain(email))
    return { status: 'unverified', reason: 'Personal or free email domain (gmail, yahoo, etc.)' };
  if (!website?.trim())
    return { status: 'unverified', reason: 'No company website provided — required for domain verification' };

  const emailDomain = (email.split('@')[1] || '').toLowerCase();
  const emailTokens = extractBrandTokens(emailDomain);
  const websiteTokens = extractBrandTokens(website);

  if (emailTokens.length > 0 && websiteTokens.length > 0 &&
      websiteTokens.some((t) => emailTokens.includes(t))) {
    return { status: 'verified' };
  }
  return { status: 'unverified', reason: 'Email domain does not match company website (no common 3+ character token found)' };
}
